import type { KnowledgeGraph } from "../knowledge/KnowledgeGraph";

export interface RecipeIngredient {
  readonly definitionId: string;
  readonly quantity: number;
}

export interface Recipe {
  readonly id: string;
  /** Knowledge node that must be unlocked for this recipe to be available. */
  readonly knowledgeNodeId: string;
  readonly ingredients: readonly RecipeIngredient[];
  readonly produces: string;
  readonly producesQuantity: number;
  readonly message: string;
}

export class RecipeRegistry {
  private readonly recipes = new Map<string, Recipe>();
  private readonly byIngredient = new Map<string, Recipe[]>();

  register(recipe: Recipe): void {
    this.recipes.set(recipe.id, recipe);
    const sorted = [...recipe.ingredients].map((i) => i.definitionId).sort();
    const key = sorted.join("+");
    const existing = this.byIngredient.get(key) ?? [];
    existing.push(recipe);
    this.byIngredient.set(key, existing);
  }

  get(id: string): Recipe | undefined {
    return this.recipes.get(id);
  }

  /** Returns all recipes the player has discovered (knowledge node unlocked). */
  getDiscovered(knowledge: KnowledgeGraph): readonly Recipe[] {
    const result: Recipe[] = [];
    for (const recipe of this.recipes.values()) {
      if (knowledge.getProgress()[recipe.knowledgeNodeId] === "understood") {
        result.push(recipe);
      }
    }
    return result;
  }

  /** Returns all registered recipes (for admin/debug purposes). */
  getAll(): readonly Recipe[] {
    return [...this.recipes.values()];
  }

  /** Finds a recipe that matches two ingredient definitionIds (order-independent). */
  findRecipe(definitionId1: string, definitionId2: string): Recipe | undefined {
    const key = [definitionId1, definitionId2].sort().join("+");
    return this.byIngredient.get(key)?.[0];
  }
}
